/**
 * CrmReferenceDetailModal — author + release a Client References letter for an Opportunity
 * (docs/REFERENCES.md). One modal handles both create (reference=null) and edit, since the fields
 * are simple: a title, an optional cover note, and an ORDERED pick from the firm-wide Reference
 * Library (services/referenceLibrary.js, curated in the CRM workspace's References tab).
 *
 * "Post & release to journey" mirrors CrmProposalDetailModal.jsx's postToJourney() recipe exactly
 * (freeze the PDF -> getOrCreateLink -> upsertEntry -> setStatus active) but is entirely
 * independent of proposal status — a reference letter can be released before or after a proposal
 * is presented, or with no proposal on the opportunity at all.
 */
import { useState, useEffect } from 'react'
import CrmModal, { F } from './CrmModal.jsx'
import { getReferenceLibrary } from '../../services/referenceLibrary.js'
import {
  createReference, updateReference, deleteReference,
  previewReferencePdf, freezeReferencePdf, markReferencePosted, unpostReference,
} from '../../services/references.js'
import { getOrCreateLink, upsertEntry, setStatus, deleteEntry } from '../../services/journey.js'
import { alertDialog, confirmDialog } from '../Dialog.jsx'

async function openPreview(tab, id) {
  try { const url = await previewReferencePdf(id, { working: true }); if (tab) tab.location = url; else window.open(url, '_blank') }
  catch (e) { if (tab) tab.close(); await alertDialog({ title: 'Preview failed', message: (e && e.message) || 'Could not generate the PDF.' }) }
}

// Default cover note for a NEW letter — still a plain editable field, not fixed boilerplate, so a
// one-off letter can say something different. Matt's draft, cleaned up per the brand voice guide
// (restrained/specific register, no em dashes) — see docs/REFERENCES.md.
const DEFAULT_INTRO = "The people below have agreed to share their experience working with Alder James, past and present. Feel free to reach out directly. A text or email is often the easiest way to start, since calls from unfamiliar numbers are sometimes screened."

export default function CrmReferenceDetailModal({ opportunityId, opportunityName, reference, onClose, onChanged }) {
  const [id, setId] = useState(reference ? reference.id : null)
  const [title, setTitle] = useState(reference ? reference.title : 'Client References')
  const [intro, setIntro] = useState(reference ? (reference.intro || '') : DEFAULT_INTRO)
  const [selected, setSelected] = useState(reference ? (reference.referenceIds || []) : [])
  const [posted, setPosted] = useState(reference ? !!reference.posted : false)
  const [journeyEntryId, setJourneyEntryId] = useState(reference ? reference.journeyEntryId : null)
  const [library, setLibrary] = useState(null)
  const [busy, setBusy] = useState(false)
  const [dirty, setDirty] = useState(false)
  const debug = typeof document !== 'undefined' && document.body.classList.contains('aj-debug')

  useEffect(() => { getReferenceLibrary().then(setLibrary).catch(() => setLibrary([])) }, [])

  function markDirty() { setDirty(true) }

  function toggleEntry(entryId) {
    if (posted) return
    setSelected(prev => prev.includes(entryId) ? prev.filter(x => x !== entryId) : [...prev, entryId])
    markDirty()
  }
  function moveEntry(entryId, direction) {
    setSelected(prev => {
      const idx = prev.indexOf(entryId)
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1
      if (idx === -1 || swapIdx < 0 || swapIdx >= prev.length) return prev
      const next = [...prev]
      ;[next[idx], next[swapIdx]] = [next[swapIdx], next[idx]]
      return next
    })
    markDirty()
  }

  /** Persists the current draft — creates on first save, updates thereafter. Returns the reference id. */
  async function save() {
    if (id) {
      await updateReference(id, { title, intro, referenceIds: selected })
      setDirty(false)
      return id
    }
    const r = await createReference(opportunityId, { title, intro, referenceIds: selected })
    setId(r.id)
    setDirty(false)
    if (onChanged) onChanged()
    return r.id
  }

  async function handlePreview() {
    // Open the blank tab SYNCHRONOUSLY, before any await — a new tab opened after an await falls
    // outside the click's user-gesture window and Chrome silently blocks it as a popup (the save()
    // below is itself an await, so opening inside openPreview() after it would be too late).
    const tab = window.open('', '_blank')
    setBusy(true)
    try { const rid = await save(); await openPreview(tab, rid) }
    catch (e) { if (tab) tab.close(); await alertDialog({ title: 'Save failed', message: e.message }) }
    finally { setBusy(false) }
  }

  async function handleDelete() {
    if (!(await confirmDialog({ title: 'Delete reference letter?', message: 'This draft reference letter will be permanently deleted.', confirmLabel: 'Delete', danger: true }))) return
    setBusy(true)
    try {
      if (id) await deleteReference(id)
      if (onChanged) onChanged()
      onClose()
    } catch (e) { await alertDialog({ title: 'Delete failed', message: e.message }) } finally { setBusy(false) }
  }

  async function postToJourney() {
    if (!selected.length) { await alertDialog({ title: 'Nothing to send', message: 'Pick at least one reference before releasing this letter.' }); return }
    const ok = await confirmDialog({
      title: 'Post & release to Client Hub?',
      message: 'This freezes the letter as a PDF and publishes it to the Client Hub for the client to view and download. It comes off the Hub once a proposal is signed. This is outward-facing.',
      confirmLabel: 'Post & release',
    })
    if (!ok) return
    setBusy(true)
    try {
      const rid = await save()
      const frozen = await freezeReferencePdf(rid)
      const { journey } = await getOrCreateLink(opportunityId, opportunityName)
      const { entry } = await upsertEntry({
        journeyId: journey.id, type: 'reference', title, published: true,
        data: { referenceId: rid, title, pdfUrl: frozen.url, intro },
      })
      await markReferencePosted(rid, { pdfUrl: frozen.url, journeyEntryId: entry.id })
      try { await setStatus(journey.id, 'active') } catch (_) {}
      setPosted(true)
      setJourneyEntryId(entry.id)
      await alertDialog({ title: 'Released', message: 'The reference letter is live in the Client Hub.' })
      if (onChanged) onChanged()
    } catch (e) {
      await alertDialog({ title: 'Post failed', message: (e && e.message) || 'Could not post the reference letter.' })
    } finally { setBusy(false) }
  }

  async function unpostTest() {
    if (!(await confirmDialog({ title: 'Unpost (test)?', message: 'Reverts posted state and pulls the entry from the Client Hub — visible in /debug mode only.', confirmLabel: 'Unpost' }))) return
    setBusy(true)
    try {
      if (journeyEntryId) { try { await deleteEntry(journeyEntryId) } catch (_) {} }
      await unpostReference(id)
      setPosted(false)
      setJourneyEntryId(null)
      if (onChanged) onChanged()
    } catch (e) { await alertDialog({ title: 'Unpost failed', message: e.message }) } finally { setBusy(false) }
  }

  const activeLibrary = (library || []).filter(e => e.active || selected.includes(e.id))

  return (
    <CrmModal title={reference ? (reference.title || 'Reference letter') : 'New reference letter'} onClose={onClose} width={620}
      footer={<>
        <button style={F.btnGhost} onClick={onClose} disabled={busy}>Close</button>
        {!posted && id && <button style={{ ...F.btnGhost, color: '#b3261e' }} onClick={handleDelete} disabled={busy}>Delete</button>}
        <button style={F.btnGhost} onClick={handlePreview} disabled={busy}>{busy ? 'Working…' : 'Preview PDF'}</button>
        {posted && debug && <button style={{ ...F.btnGhost, color: '#b8540a' }} onClick={unpostTest} disabled={busy} title="Test only — visible in /debug mode">↩ Unpost (test)</button>}
        {!posted && <button style={F.btnPrimary} onClick={postToJourney} disabled={busy}>{busy ? 'Posting…' : 'Post & release'}</button>}
      </>}>
      {posted && (
        <div style={{ marginBottom: 14, padding: '8px 10px', borderRadius: 5, background: 'rgba(31,157,87,0.1)', color: '#1f9d57', fontFamily: '"DM Sans",sans-serif', fontSize: 12 }}>
          Live in the Client Hub. Editing is locked — unpost first (debug mode) to make changes.
        </div>
      )}
      <div style={F.row}>
        <span style={F.label}>Title</span>
        <input style={F.input} value={title} disabled={posted}
          onChange={e => { setTitle(e.target.value); markDirty() }} />
      </div>
      <div style={F.row}>
        <span style={F.label}>Cover note (optional, shown above the references)</span>
        <textarea style={F.textarea} value={intro} disabled={posted}
          placeholder="A short note introducing these references…"
          onChange={e => { setIntro(e.target.value); markDirty() }} />
      </div>

      <div style={{ ...F.row, marginTop: 4 }}>
        <span style={F.label}>References to include ({selected.length} selected)</span>
        {library === null ? (
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', padding: '8px 0' }}>Loading reference library…</div>
        ) : activeLibrary.length === 0 ? (
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', padding: '8px 0' }}>
            No references in the firm library yet — add some in the CRM workspace's References tab.
          </div>
        ) : (
          <div style={{ border: '0.5px solid var(--aj-border-mid)', borderRadius: 5, maxHeight: 260, overflowY: 'auto' }}>
            {activeLibrary.map(e => {
              const isSel = selected.includes(e.id)
              const pos = selected.indexOf(e.id)
              return (
                <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderBottom: '0.5px solid var(--aj-border)', cursor: posted ? 'default' : 'pointer' }}>
                  <input type="checkbox" checked={isSel} disabled={posted} onChange={() => toggleEntry(e.id)} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)' }}>{e.name}</span>
                    {(e.projects || []).map((p, i) => (
                      <span key={i} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', marginLeft: 8 }}>{[p.projectType, p.location].filter(Boolean).join('  —  ')}</span>
                    ))}
                    {!e.active && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)', marginLeft: 8, textTransform: 'uppercase' }}>Inactive</span>}
                  </div>
                  {isSel && !posted && (
                    <>
                      <button type="button" onClick={ev => { ev.preventDefault(); moveEntry(e.id, 'up') }} disabled={pos === 0}
                        style={{ background: 'none', border: 'none', cursor: pos === 0 ? 'default' : 'pointer', color: pos === 0 ? 'var(--aj-border-mid)' : 'var(--aj-text-muted)', fontSize: 11, padding: '0 2px' }}>▲</button>
                      <button type="button" onClick={ev => { ev.preventDefault(); moveEntry(e.id, 'down') }} disabled={pos === selected.length - 1}
                        style={{ background: 'none', border: 'none', cursor: pos === selected.length - 1 ? 'default' : 'pointer', color: pos === selected.length - 1 ? 'var(--aj-border-mid)' : 'var(--aj-text-muted)', fontSize: 11, padding: '0 2px' }}>▼</button>
                    </>
                  )}
                </label>
              )
            })}
          </div>
        )}
      </div>
      {dirty && !posted && (
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', fontStyle: 'italic' }}>
          Unsaved changes are saved automatically when you preview or release.
        </div>
      )}
    </CrmModal>
  )
}
